<template>
	<a
		target="_blank"
		:rel="linkRel"
		:href="offerUrl"
		data-allow-mismatch="attribute"
		@click.capture="onClickout($event)"
	>
		<slot />
	</a>
</template>

<script setup lang="ts">
import { computed, shallowRef, triggerRef } from 'vue';

import { handleClickoutUrl, isClickoutUrl, UrlHelper, trackClickout } from '@/helpers/clickout-helper';
import { ClickoutType } from '@/interfaces/snowplow/clickout-context';

const props = withDefaults(
	defineProps<{
		/** URl of the offer, should be a ClickoutURL link. */
		href: UrlHelper;

		/** Value for the link's `rel` attribute */
		rel?: string;

		/** Whether to skip the pre-affiliation check */
		skipPreAffiliationCheck?: boolean;

		/** Clickout Type */
		clickoutType?: ClickoutType;
	}>(),
	{ rel: 'noopener' }
);

const emits = defineEmits<{ (e: 'click', event: MouseEvent): void }>();

const enableSkipUct = computed(() => false);

const CLICK_MASKED_SHORTNAMES = ['tbv'];
const linkRel = computed(() =>
	isClickoutUrl(props.href) && CLICK_MASKED_SHORTNAMES.includes(props.href.shortName ?? '')
		? `${props.rel ?? ''} noreferrer`
		: props.rel
);

/**
 * We create the reacitiveKey to trigger reactivity updates when the ClickoutUrl changes.
 * The setTriggerRef function is used to trigger reactivity when the contexts update.
 * This is necessary because Vue's reactivity system does not automatically track changes in class instances.
 */
const reactiveKey = shallowRef(null);

/**
 * reactiveUrl returns a computed property that reacts to updates in the ClickoutUrl.
 */
const reactiveUrl = computed(() => {
	if (!isClickoutUrl(props.href)) return props.href;

	// watch for changes to the key from updates to the ClickoutUrl
	reactiveKey.value;

	// set the triggerRef to update the reactive state when certain ClickoutUrl properties change
	return props.href.setTriggerRef(updateReactiveKey);
});

const offerUrl = computed(() => {
	if (enableSkipUct.value) {
		if (isClickoutUrl(reactiveUrl.value) && reactiveUrl.value.preAffiliatedUrl) {
			return reactiveUrl.value.preAffiliatedUrl;
		}
		return reactiveUrl.value.get('r') ?? reactiveUrl.value.toString();
	}

	// return the directly affiliated link
	return reactiveUrl.value.toString();
});

const client = useNuxtApp().$apolloClients.default!;

function onClickout(event: MouseEvent) {
	emits('click', event);

	if (!enableSkipUct.value) {
		if (isClickoutUrl(reactiveUrl.value)) {
			trackClickout(reactiveUrl.value);
		}
		return;
	}

	// Skip UCT is active, prevent default <a> behavior and take over with our `handleClickoutUrl` logic
	event.preventDefault();

	handleClickoutUrl(reactiveUrl.value, client, enableSkipUct.value, !props.skipPreAffiliationCheck);
}

/**
 * The function passed to ClickoutUrl.setTriggerRef to update the reactiveKey.
 * this function does not work inline, but is stable when declared
 * triggerRef is used to trigger reactivity updates in Vue, without the need to update the value of the shallowRef directly.
 *
 */
function updateReactiveKey() {
	triggerRef(reactiveKey);
}
</script>
